// Record preview: a panel that slides in over the page with the record's key facts and its lines, so you
// can check a document without leaving where you are. Then open it in courageMFG or in Business Central.
import { useEffect, useRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api, ApiError, type BcDocPreview } from "./api";
import { useLoad } from "./hooks";
import { bcUrl } from "./config";
import { ErrorBox, label } from "./ui";
import { STAGE_NAME } from "./crm";

export type PeekTarget =
  | { type: "opportunity"; number: string }
  | { type: "quote"; number: string }
  | { type: "bc"; kind: BcDocPreview["kind"]; id: string; number: string };

const usd = (n: number | null | undefined) => (n === null || n === undefined ? "" : n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const qty = (n: number | null | undefined) => (n === null || n === undefined ? "" : n.toLocaleString("en-US", { maximumFractionDigits: 2 }));
const pct = (m: number | null | undefined) => (m === null || m === undefined ? "" : (m * 100).toFixed(1));
const usDate = (d: string | null | undefined) => (d ? new Date(d.slice(0, 10) + "T00:00:00").toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "");
const BC_PAGE: Record<string, number> = { order: 42, quote: 41, invoice: 132, creditMemo: 134, shipment: 130 };
const bcDoc = (page: number, no: string) => `${bcUrl(page)}&filter=${encodeURIComponent(`'No.' IS '${no}'`)}`;

function Facts({ items }: { items: [string, ReactNode, string?][] }) {
  return <dl className="peek-facts">{items.filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([k, v, cls]) => <div key={k}><dt>{k}</dt><dd className={cls}>{v}</dd></div>)}</dl>;
}

function Lines({ rows, order, floor }: { rows: { key: string; no: string; desc: string; qty: number; uom?: string | null; price: number | null; cost: number | null; margin: number | null; amount: number | null; shipped?: number | null; outstanding?: number | null; date?: string | null }[]; order?: boolean; floor?: number }) {
  if (!rows.length) return <p className="dash-empty" style={{ margin: 0 }}>No lines.</p>;
  return (
    <div className="table-wrap">
      <table className="dgrid num-grid peek-lines">
        <thead><tr><th>No.</th><th>Description</th><th className="r">Quantity</th>{order && <th className="r">Shipped</th>}{order && <th className="r">Outstanding</th>}<th className="r">Unit Price</th><th className="r">Unit Cost</th><th className="r">Margin %</th><th className="r">Amount</th></tr></thead>
        <tbody>{rows.map((l) => (
          <tr key={l.key}>
            <td>{l.no}</td><td className="wrap">{l.desc}{l.date && order ? <div className="muted">Ships {usDate(l.date)}</div> : null}</td>
            <td className="r">{qty(l.qty)}{l.uom ? ` ${l.uom}` : ""}</td>
            {order && <td className="r">{qty(l.shipped)}</td>}{order && <td className={`r ${l.outstanding ? "warn" : ""}`}>{qty(l.outstanding)}</td>}
            <td className="r">{usd(l.price)}</td><td className="r">{usd(l.cost)}</td>
            <td className={`r ${l.margin !== null && floor !== undefined && l.margin < floor ? "bad" : ""}`}>{pct(l.margin)}</td><td className="r">{usd(l.amount)}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function BcPeek({ t }: { t: Extract<PeekTarget, { type: "bc" }> }) {
  const { data: d, error } = useLoad(() => api.bcDocument(t.kind, t.id), [t.kind, t.id]);
  const code = error instanceof ApiError ? error.body?.code : undefined;
  if (error) return <Body title={`${t.number}`} sub="Business Central"><ErrorBox error={code === "consent_required" ? new Error("Business Central needs an admin to grant courageMFG access.") : error} /></Body>;
  if (!d) return <Body title={t.number} sub="Loading from Business Central…"><div className="peek-skel" /></Body>;
  const order = d.kind === "order";
  const shippedPct = order ? (() => { const q = d.lines.reduce((a, l) => a + l.quantity, 0); const s = d.lines.reduce((a, l) => a + (l.shipped ?? 0), 0); return q ? s / q : null; })() : null;
  return (
    <Body title={`${d.label} ${d.number}`} sub={[d.customerName, d.customerNumber].filter(Boolean).join(", ")} status={d.status}
      actions={<>
        {d.linked && <Link className="btn" to={d.linked.opportunity ? `/opportunities/${d.linked.opportunity}` : `/quotes/${d.linked.quote}`}>Open in courageMFG</Link>}
        <a className="btn primary" href={bcDoc(d.bcPage, d.number)} target="_blank" rel="noopener noreferrer">Open in Business Central</a>
      </>}>
      {d.daysOverdue ? <div className="notify warn"><span>{usd(d.remaining)} {d.currency} is {d.daysOverdue} days past the due date.</span></div> : null}
      <Facts items={[
        ["Date", usDate(d.date)],
        [d.kind === "quote" ? "Valid Until" : "Due Date", usDate(d.kind === "quote" ? d.validUntil : d.dueDate)],
        ["Requested Delivery", usDate(d.requestedDeliveryDate)],
        ["Amount Excl. Tax", d.subtotal !== null ? `${usd(d.subtotal)} ${d.currency}` : ""],
        ["Amount Incl. Tax", d.total !== null ? `${usd(d.total)} ${d.currency}` : ""],
        ["Remaining", d.remaining !== null && d.kind === "invoice" ? `${usd(d.remaining)} ${d.currency}` : "", d.daysOverdue ? "bad" : ""],
        ["Margin", d.margin !== null ? `${pct(d.margin)}% (${d.costBasis === "posted" ? "posted cost" : "item unit cost"})` : ""],
        ["Shipped", shippedPct !== null ? `${Math.round(shippedPct * 100)}% of quantity` : ""],
        ["External Document No.", d.externalDocumentNumber ?? ""],
        ["courageMFG", d.linked ? <>{d.linked.opportunity ? <><Link to={`/opportunities/${d.linked.opportunity}`}>{d.linked.opportunity}</Link> {d.linked.opportunityName}, </> : null}quote <Link to={`/quotes/${d.linked.quote}`}>{d.linked.quote}</Link></> : ""],
        ["Ship-to", d.shipTo ?? ""],
      ]} />
      <h3 className="sub-h2">Lines</h3>
      <Lines order={order} floor={0.3} rows={d.lines.map((l, i) => ({ key: `${l.sequence ?? i}`, no: l.number, desc: l.description, qty: l.quantity, uom: l.uom, price: l.unitPrice, cost: l.unitCost, margin: l.margin, amount: l.amount, shipped: l.shipped, outstanding: l.outstanding, date: l.shipmentDate }))} />
      {d.costBasis && <p className="peek-note">{d.costBasis === "posted" ? "Unit cost is the cost posted in Business Central value entries for this document, including cost adjustments copied so far." : "Unit cost is each item's current unit cost in Business Central; the posted cost is known once the order is invoiced."}</p>}
    </Body>
  );
}

function QuotePeek({ number }: { number: string }) {
  const { data: q, error } = useLoad(() => api.quote(number), [number]);
  if (error) return <Body title={number}><ErrorBox error={error} /></Body>;
  if (!q) return <Body title={`Quote ${number}`} sub="Loading…"><div className="peek-skel" /></Body>;
  return (
    <Body title={`Quote ${q.number}`} sub={[q.account.name, q.opportunity?.name].filter(Boolean).join(", ")} status={label(q.state)}
      actions={<>
        <Link className="btn primary" to={`/quotes/${q.number}`}>Open in courageMFG</Link>
        {q.bcQuoteNumber && <a className="btn" href={bcDoc(BC_PAGE.quote, q.bcQuoteNumber)} target="_blank" rel="noopener noreferrer">Open in Business Central</a>}
      </>}>
      {q.margin !== null && q.margin < q.marginFloor && <div className="notify warn"><span>Margin {pct(q.margin)}% is below the {pct(q.marginFloor)}% minimum.</span></div>}
      <Facts items={[
        ["Amount Excl. Tax", `${usd(q.total)} USD`], ["Cost", q.cost !== null ? `${usd(q.cost)} USD` : "Some lines have no cost"], ["Margin", q.margin !== null ? `${pct(q.margin)}%` : ""],
        ["Valid Until", usDate(q.validUntil)], ["Revision", q.revision], ["Primary", q.isPrimary ? "Yes" : "No"],
        ["Opportunity", q.opportunity ? <Link to={`/opportunities/${q.opportunity.number}`}>{q.opportunity.number} {q.opportunity.name}</Link> : ""],
        ["BC Sales Quote", q.bcQuoteNumber ?? "Not issued"], ["BC Sales Order", q.bcOrderNumber ?? ""],
      ]} />
      <h3 className="sub-h2">Lines</h3>
      <Lines floor={q.marginFloor} rows={q.lines.map((l) => { const qq = Number(l.quantity), p = Number(l.unitPrice), c = l.unitCost === null ? null : Number(l.unitCost); return { key: l.id, no: l.itemNumber ?? "", desc: l.description, qty: qq, price: p, cost: c, margin: c === null || !p ? null : (p - c) / p, amount: qq * p }; })} />
    </Body>
  );
}

function OpportunityPeek({ number }: { number: string }) {
  const { data: o, error } = useLoad(() => api.opportunity(number), [number]);
  const primary = o?.quotes.find((x) => x.isPrimary);
  const { data: q } = useLoad(() => (primary ? api.quote(primary.id) : Promise.resolve(null)), [primary?.id]);
  if (error) return <Body title={number}><ErrorBox error={error} /></Body>;
  if (!o) return <Body title={number} sub="Loading…"><div className="peek-skel" /></Body>;
  const next = o.activities.filter((a) => a.kind === "task" && !a.doneAt).sort((a, b) => ((a.dueDate ?? "9") < (b.dueDate ?? "9") ? -1 : 1))[0];
  const decider = o.committee.find((m) => m.role === "decision_maker") ?? o.committee[0];
  return (
    <Body title={`${o.number} · ${o.name}`} sub={o.account.name} status={STAGE_NAME[o.stage] ?? label(o.stage)}
      actions={<>
        <Link className="btn primary" to={`/opportunities/${o.number}`}>Open in courageMFG</Link>
        {o.bcOrderNumber && <a className="btn" href={bcDoc(BC_PAGE.order, o.bcOrderNumber)} target="_blank" rel="noopener noreferrer">Open Sales Order in Business Central</a>}
      </>}>
      <Facts items={[
        ["Estimated Value", o.amount !== null ? `${usd(o.amount)} USD` : ""], ["Probability", o.probability !== null ? `${o.probability}%` : ""],
        ["Expected Closing Date", usDate(o.closeDate)], ["Next Step", next ? `${next.subject}${next.dueDate ? ` by ${usDate(next.dueDate)}` : ""}` : "None"],
        ["Decision Maker", decider ? `${decider.name}${decider.title ? `, ${decider.title}` : ""}` : ""], ["Competitor", o.competitor ?? ""],
        ["BC Sales Order", o.bcOrderNumber ?? ""],
      ]} />
      {primary && <><h3 className="sub-h2">Primary quote {primary.number}, {label(primary.state)}{q?.margin != null ? `, ${pct(q.margin)}% margin` : ""}</h3>
        {q ? <Lines floor={q.marginFloor} rows={q.lines.map((l) => { const qq = Number(l.quantity), p = Number(l.unitPrice), c = l.unitCost === null ? null : Number(l.unitCost); return { key: l.id, no: l.itemNumber ?? "", desc: l.description, qty: qq, price: p, cost: c, margin: c === null || !p ? null : (p - c) / p, amount: qq * p }; })} /> : <div className="peek-skel" />}</>}
      {!primary && <p className="dash-empty" style={{ margin: 0 }}>No quote yet.</p>}
    </Body>
  );
}

let closeCurrent: (() => void) | null = null;
function Body({ title, sub, status, actions, children }: { title: string; sub?: string; status?: string | null; actions?: ReactNode; children: ReactNode }) {
  return (
    <>
      <div className="peek-h">
        <div style={{ minWidth: 0 }}>
          <h2 id="peek-title">{title}</h2>
          {(sub || status) && <div className="peek-sub">{sub}{sub && status ? " · " : ""}{status && <span className="peek-status">{status}</span>}</div>}
        </div>
        <button className="icon-btn" aria-label="Close preview" onClick={() => closeCurrent?.()}>
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.6" /></svg>
        </button>
      </div>
      <div className="peek-body">{children}</div>
      {actions && <div className="peek-actions">{actions}</div>}
    </>
  );
}

export function Peek({ target, onClose }: { target: PeekTarget | null; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  closeCurrent = onClose;
  useEffect(() => {
    if (!target) return;
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", key);
    return () => { window.removeEventListener("keydown", key); prev?.focus?.(); };
  }, [target?.type, (target as any)?.number]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!target) return null;
  return (
    <div className="peek-wrap" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="peek" role="dialog" aria-modal="true" aria-labelledby="peek-title" tabIndex={-1} ref={ref}>
        {target.type === "bc" ? <BcPeek t={target} /> : target.type === "quote" ? <QuotePeek number={target.number} /> : <OpportunityPeek number={target.number} />}
      </div>
    </div>
  );
}
